import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  api,
  COMPANY_LABEL,
  STATUS_LABEL,
  tagList,
  type Customer,
  type CustomerStatus,
} from '../components/api'
import {
  Alert,
  Badge,
  Card,
  EmptyState,
  FormField,
  Input,
  Select,
} from '../components/ui'

export function CustomerListPage() {
  const [items, setItems] = useState<Customer[]>([])
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [q, setQ] = useState('')
  const [status, setStatus] = useState<'' | CustomerStatus>('')
  const [tag, setTag] = useState('')
  const [assignee, setAssignee] = useState('')

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    api<{ items: Customer[] }>('/api/customers', {
      query: { status: status || undefined, q: q || undefined },
    })
      .then((d) => {
        if (!cancelled) setItems(d.items ?? [])
      })
      .catch((e: Error) => {
        if (!cancelled) setError(e.message)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [status, q])

  const tagOptions = useMemo(() => {
    const all = new Set<string>()
    for (const c of items) for (const t of tagList(c.tags)) all.add(t)
    return Array.from(all).sort()
  }, [items])

  const assigneeOptions = useMemo(() => {
    const all = new Map<string, string>()
    for (const c of items) {
      if (c.sales_user_id)
        all.set(
          String(c.sales_user_id),
          c.sales_user_name ?? `#${c.sales_user_id}`,
        )
      if (c.dev_user_id)
        all.set(
          String(c.dev_user_id),
          c.dev_user_name ?? `#${c.dev_user_id}`,
        )
    }
    return Array.from(all.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name, 'ja'))
  }, [items])

  const filtered = useMemo(() => {
    return items.filter((c) => {
      if (tag && !tagList(c.tags).includes(tag)) return false
      if (
        assignee &&
        String(c.sales_user_id) !== assignee &&
        String(c.dev_user_id) !== assignee
      )
        return false
      return true
    })
  }, [items, tag, assignee])

  return (
    <>
      <div className="page-header">
        <h1>顧客一覧</h1>
        <Link to="/customers/new" className="btn btn-primary btn-sm">
          ＋ 新規顧客を登録
        </Link>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <Card>
        <div className="form-row" style={{ marginBottom: 4 }}>
          <FormField label="検索（社名・請求名・タグ）" htmlFor="q">
            <Input
              id="q"
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="株式会社…"
            />
          </FormField>
          <FormField label="状態" htmlFor="status">
            <Select
              id="status"
              value={status}
              onChange={(e) => setStatus(e.target.value as '' | CustomerStatus)}
            >
              <option value="">すべて</option>
              {(Object.keys(STATUS_LABEL) as CustomerStatus[]).map((s) => (
                <option key={s} value={s}>
                  {STATUS_LABEL[s]}
                </option>
              ))}
            </Select>
          </FormField>
          <FormField label="タグ" htmlFor="tag">
            <Select
              id="tag"
              value={tag}
              onChange={(e) => setTag(e.target.value)}
            >
              <option value="">すべて</option>
              {tagOptions.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </Select>
          </FormField>
          <FormField label="担当者" htmlFor="assignee">
            <Select
              id="assignee"
              value={assignee}
              onChange={(e) => setAssignee(e.target.value)}
            >
              <option value="">すべて</option>
              {assigneeOptions.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </Select>
          </FormField>
        </div>
        <small className="muted">
          {loading ? '読み込み中…' : `${filtered.length} 件 / 全 ${items.length} 件`}
        </small>
      </Card>

      <Card>
        {loading ? (
          <p className="muted">読み込み中…</p>
        ) : filtered.length === 0 ? (
          <EmptyState
            title="該当する顧客がありません"
            description="フィルタ条件を緩めるか、新規登録してください。"
            action={
              <Link to="/customers/new" className="btn btn-primary btn-sm">
                ＋ 新規顧客を登録
              </Link>
            }
          />
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>顧客名</th>
                <th style={{ width: 80 }}>状態</th>
                <th>業種・タグ</th>
                <th style={{ width: 160 }}>営業担当</th>
                <th style={{ width: 160 }}>制作担当</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <tr key={c.id}>
                  <td>
                    <Link to={`/customers/${c.id}`}>{c.company_name}</Link>
                    {c.billing_name && c.billing_name !== c.company_name && (
                      <div className="subtle" style={{ fontSize: 11 }}>
                        請求名：{c.billing_name}
                      </div>
                    )}
                  </td>
                  <td>
                    <Badge
                      variant={
                        c.status === 'active'
                          ? 'success'
                          : c.status === 'paused'
                            ? 'warning'
                            : 'muted'
                      }
                    >
                      {STATUS_LABEL[c.status]}
                    </Badge>
                  </td>
                  <td>
                    {c.industry && (
                      <div style={{ fontSize: 12 }}>{c.industry}</div>
                    )}
                    <div
                      style={{
                        display: 'flex',
                        gap: 4,
                        flexWrap: 'wrap',
                        marginTop: 2,
                      }}
                    >
                      {tagList(c.tags).map((t) => (
                        <Badge key={t} variant="muted">
                          {t}
                        </Badge>
                      ))}
                    </div>
                  </td>
                  <td>
                    {c.sales_user_name ?? '—'}
                    {c.sales_user_company && (
                      <div className="subtle" style={{ fontSize: 11 }}>
                        {COMPANY_LABEL[c.sales_user_company]}
                      </div>
                    )}
                  </td>
                  <td>
                    {c.dev_user_name ?? '—'}
                    {c.dev_user_company && (
                      <div className="subtle" style={{ fontSize: 11 }}>
                        {COMPANY_LABEL[c.dev_user_company]}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </>
  )
}
